import React from 'react';
import { styled } from '@mui/material';
import { Colors, Text } from 'library';
import { ChevronRightRounded, RadioButtonUncheckedOutlined, CheckCircle } from '@mui/icons-material';
import type { DashboardSummary } from '../types';
import { ProgressItem } from './ProgressItem';
import LOGO from 'assets/images/tandem-blocks-logo.svg';
import { PaymentSetupRow } from './PaymentSetupRow';
import { useModalsContext } from 'ModalsContext';
import { PermissionType, useUserContext } from 'UserContext';

export function OnboardProgress({ data, refresh }: { data: DashboardSummary; refresh: () => void }) {
    const { openJobForm } = useModalsContext();
    const { hasPermission } = useUserContext();
    const canPostJobs = hasPermission(PermissionType.PostJob);
    const is_location_setup = data.centers_details.some((center) => center.progress >= 70);

    function onPostJobClick() {
        if (!canPostJobs) {
            return;
        }

        openJobForm('How It Works');
    }

    const completeIcons = (
        <IconContainer>
            <CheckCircle htmlColor={Colors.mediumGrey} style={{ height: 28, width: 28 }} />
            <Text>Complete</Text>
        </IconContainer>
    );

    return (
        <CardBody>
            <Inner>
                <Text variant="h1" bold>
                    Finish Account Set Up
                </Text>
                <ProgressItem
                    text="Create an Account"
                    icon={
                        <IconContainer>
                            <CheckCircle htmlColor={Colors.mediumGrey} style={{ height: 28, width: 28 }} />
                            <Text>Complete</Text>
                        </IconContainer>
                    }
                />
                {data?.payment?.status === 'complete' ? (
                    <ProgressItem
                        text="Add a Payment Method"
                        icon={
                            <IconContainer>
                                <CheckCircle htmlColor={Colors.mediumGrey} style={{ height: 28, width: 28 }} />
                                <Text>Complete</Text>
                            </IconContainer>
                        }
                    />
                ) : (
                    <PaymentSetupRow status={data?.payment?.status} refresh={refresh} />
                )}
                <ProgressItem
                    text="Post a Job (Substitute or Permanent Role)"
                    backgroundColor={Colors.lightTurq}
                    onClick={canPostJobs ? onPostJobClick : undefined}
                    icon={
                        <IconContainer>
                            <RadioButtonUncheckedOutlined
                                htmlColor={Colors.mediumGrey}
                                style={{ height: 28, width: 28 }}
                            />
                            <Text>To Do</Text>
                            <ChevronRightRounded htmlColor={Colors.darkNavy} style={{ height: 36, width: 36 }} />
                        </IconContainer>
                    }
                />
                <ProgressItem
                    text="Add Center Information"
                    backgroundColor={is_location_setup ? undefined : Colors.lightTurq}
                    to={is_location_setup ? '' : '/manage-centers'}
                    icon={
                        is_location_setup ? (
                            completeIcons
                        ) : (
                            <IconContainer>
                                <RadioButtonUncheckedOutlined
                                    htmlColor={Colors.mediumGrey}
                                    style={{ height: 28, width: 28 }}
                                />
                                <Text>To Do</Text>
                                <ChevronRightRounded htmlColor={Colors.darkNavy} style={{ height: 36, width: 36 }} />
                            </IconContainer>
                        )
                    }
                />
                <ProgressItem
                    text="Tandem Will Match You with Staff"
                    backgroundColor={Colors.lightTurq}
                    icon={<LOGO htmlColor={Colors.darkNavy} style={{ height: 36, width: 36 }} />}
                />
            </Inner>
        </CardBody>
    );
}

const CardBody = styled('div')({
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
    width: '100%',
});

const Inner = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
});

const IconContainer = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: 'fit-content',
});
